// @ts-nocheck
/**
 * CollectionCard - コレクション画面用カードコンポーネント
 *
 * CardV2 + RarityEffectLayer をラップし、コレクション固有のUIを追加
 * - ヘッダー: コスト、レアリティバッジ
 * - ボディ: カード名、ステータス（モンスターのみ）
 * - フッター: タイプアイコン、所持枚数
 * - 価格行: ティアバッジ、価格
 */

import React, { memo } from 'react';
import CardV2 from './CardV2';
import RarityEffectLayer from './RarityEffectLayer';
import { CARD_DISPLAY_SIZES } from './cardConstants';
import { ATTRIBUTE_COLORS } from '../../utils/constants';
import { RARITY_COLORS } from '../../collection/data/constants';

// ========================================
// 型定義
// ========================================

export interface CollectionCardProps {
  /** カードデータ */
  card: {
    id: string;
    name: string;
    attribute: string;
    cost: number;
    type: string;
    attack?: number;
    hp?: number;
    category?: string;
    effect?: string;
  };

  /** レアリティ */
  rarity: string;

  /** 所持枚数 */
  quantity: number;

  /** 価格 */
  price?: number;

  /** ティア */
  tier?: 'S' | 'A' | 'B' | 'C' | 'D';

  /** エフェクトレベル */
  effectLevel?: 'full' | 'minimal' | 'off';

  /** クリックハンドラ */
  onClick?: () => void;

  /** モバイル表示 */
  isMobile?: boolean;
}

// ========================================
// 定数
// ========================================

const TYPE_ICONS = {
  monster: '⚔️',
  magic: '✨',
  field: '🏔️',
  phasecard: '🔮',
};

const TIER_COLORS = {
  S: '#ff4444',
  A: '#ff9900',
  B: '#3498db',
  C: '#2ecc71',
  D: '#808080',
};

// ========================================
// スタイル定義
// ========================================

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    cursor: 'pointer',
    transition: 'transform 0.2s ease',
  },
  cardWrapper: {
    position: 'relative' as const,
    overflow: 'hidden',
    borderRadius: '8px',
  },
  header: {
    padding: '4px 6px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'rgba(0,0,0,0.5)',
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
  },
  costBadge: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #6b4ce6 0%, #9d4ce6 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: 'bold' as const,
    color: '#fff',
  },
  rarityBadge: {
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 'bold' as const,
    color: '#fff',
  },
  footer: {
    padding: '4px 6px',
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'absolute' as const,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 10,
  },
  typeIcon: {
    fontSize: '12px',
  },
  quantityBadge: {
    fontSize: '11px',
    fontWeight: 'bold' as const,
    color: '#ffd700',
  },
  infoSection: {
    padding: '6px 4px 6px',
    background: 'rgba(20, 20, 40, 0.95)',
    borderRadius: '0 0 4px 4px',
    marginTop: '2px',
  },
  cardName: {
    fontSize: '11px',
    fontWeight: 'bold' as const,
    textAlign: 'center' as const,
    color: '#fff',
    textShadow: '0 1px 2px rgba(0,0,0,0.5)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    lineHeight: '1.4',
  },
  cardStats: {
    fontSize: '10px',
    color: 'rgba(255,255,255,0.8)',
    textAlign: 'center' as const,
    marginTop: '3px',
    lineHeight: '1.2',
  },
  priceRow: {
    padding: '4px 6px',
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: '0 0 4px 4px',
  },
  tierBadge: {
    fontSize: '10px',
    fontWeight: 'bold' as const,
    padding: '1px 4px',
    borderRadius: '3px',
    color: '#fff',
  },
  priceBadge: {
    fontSize: '10px',
    color: '#ffd700',
  },
};

// ========================================
// メインコンポーネント
// ========================================

const CollectionCard: React.FC<CollectionCardProps> = memo(({
  card,
  rarity,
  quantity,
  price = 0,
  tier = 'D',
  effectLevel = 'full',
  onClick,
  isMobile = false,
}) => {
  const colors = ATTRIBUTE_COLORS[card.attribute] || ATTRIBUTE_COLORS['なし'];
  const rarityColor = RARITY_COLORS[rarity] || '#808080';
  const tierColor = TIER_COLORS[tier] || TIER_COLORS.D;
  const isMonster = card.type === 'monster';

  // サイズ計算
  const sizePreset = isMobile
    ? CARD_DISPLAY_SIZES.collectionMobile
    : CARD_DISPLAY_SIZES.collection;
  const cardWidth = sizePreset.width;
  const cardHeight = sizePreset.height;

  // カード画像部分のアスペクト比を維持（3:4）
  const imageHeight = Math.floor(cardWidth * (4 / 3));

  return (
    <div
      style={{
        ...styles.container,
        width: cardWidth,
      }}
      onClick={onClick}
    >
      {/* カード画像部分（RarityEffectLayer + CardV2） */}
      <RarityEffectLayer
        rarity={rarity}
        effectLevel={effectLevel}
        width={cardWidth}
        height={imageHeight}
        enableMouseEffect={effectLevel === 'full'}
        hoverTransform="translateY(-4px) scale(1.02)"
      >
        {/* ヘッダー: コスト & レアリティ */}
        <div style={styles.header}>
          <div style={styles.costBadge}>{card.cost}</div>
          <div
            style={{
              ...styles.rarityBadge,
              background: rarityColor,
            }}
          >
            {rarity}
          </div>
        </div>

        {/* CardV2（画像のみ） */}
        <CardV2
          card={card}
          displayMode="collection"
          displayWidth={cardWidth}
          isMobile={isMobile}
        />

        {/* フッター: タイプ & 所持枚数 */}
        <div style={styles.footer}>
          <span style={styles.typeIcon}>
            {TYPE_ICONS[card.type] || '📄'}
          </span>
          <span style={styles.quantityBadge}>×{quantity}</span>
        </div>
      </RarityEffectLayer>

      {/* 情報セクション: 名前 & ステータス */}
      <div style={styles.infoSection}>
        <div style={styles.cardName}>{card.name}</div>
        {isMonster && (
          <div style={styles.cardStats}>
            ATK {card.attack} / HP {card.hp}
          </div>
        )}
      </div>

      {/* 価格行: ティア & 価格 */}
      <div style={styles.priceRow}>
        <span
          style={{
            ...styles.tierBadge,
            background: tierColor,
          }}
        >
          {tier}
        </span>
        <span style={styles.priceBadge}>{price.toLocaleString()}G</span>
      </div>
    </div>
  );
});

CollectionCard.displayName = 'CollectionCard';

export default CollectionCard;
